import React, { useState, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  FormControl,
  FormHelperText,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  Phone,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { validatePhoneNumber, formatPhoneForDisplay } from '@/utils/phone-validation';

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string, isValid: boolean) => void;
  onBlur?: () => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  margin?: 'none' | 'dense' | 'normal';
  fullWidth?: boolean;
  showValidationStatus?: boolean;
  defaultCountry?: string;
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  onBlur,
  label = 'Phone Number',
  placeholder = '+1 (555) 123-4567',
  error = false,
  helperText,
  disabled = false,
  required = false,
  autoFocus = false,
  margin = 'normal',
  fullWidth = true,
  showValidationStatus = true,
  defaultCountry = 'US'
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [validation, setValidation] = useState(validatePhoneNumber(value, defaultCountry));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const newValidation = validatePhoneNumber(value, defaultCountry);
    setValidation(newValidation);
    
    // Format display value when not focused
    if (!isFocused && newValidation.isValid && newValidation.formatted) {
      setDisplayValue(formatPhoneForDisplay(newValidation.formatted));
    } else if (!isFocused) {
      setDisplayValue(value);
    }
  }, [value, defaultCountry, isFocused]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setDisplayValue(inputValue);
    
    // Validate the input
    const validation = validatePhoneNumber(inputValue, defaultCountry);
    setValidation(validation);
    
    // Call onChange with the raw input and validation status
    onChange(inputValue, validation.isValid);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw input when focused
    setDisplayValue(value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    
    // Format display value when losing focus
    if (validation.isValid && validation.formatted) {
      setDisplayValue(formatPhoneForDisplay(validation.formatted));
      // Update parent with formatted value
      onChange(validation.formatted, true);
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  const getValidationIcon = () => {
    if (!showValidationStatus || !value) return null;
    
    if (validation.isValid) {
      return <CheckCircle color="success" fontSize="small" />;
    } else if (value.length > 0) {
      return <Warning color="warning" fontSize="small" />;
    }
    
    return null;
  };

  const getHelperText = () => {
    if (helperText) return helperText;
    
    if (validation.error && value.length > 0) {
      return validation.error;
    }
    
    if (!value) {
      return 'Enter with country code (e.g., +1 for US)';
    }
    
    if (validation.isValid && validation.country) {
      return `Valid ${validation.country} phone number`;
    }
    
    return 'Enter with country code (e.g., +1 for US)';
  };

  const hasError = error || (validation.error !== undefined && value.length > 0);

  return (
    <FormControl fullWidth={fullWidth} margin={margin} error={hasError}>
      <TextField
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        label={label}
        placeholder={placeholder}
        type="tel"
        autoComplete="tel"
        autoFocus={autoFocus}
        disabled={disabled}
        required={required}
        error={hasError}
        helperText={getHelperText()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Phone color="action" />
            </InputAdornment>
          ),
          endAdornment: showValidationStatus ? (
            <InputAdornment position="end">
              {getValidationIcon()}
            </InputAdornment>
          ) : undefined,
        }}
      />
      
      {/* Additional validation info */}
      {showValidationStatus && validation.isValid && validation.formatted && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<CheckCircle />}
            label={`${validation.country || 'International'} Format`}
            size="small"
            color="success"
            variant="outlined"
          />
          {!isFocused && (
            <Typography variant="caption" color="text.secondary">
              Formatted: {formatPhoneForDisplay(validation.formatted)}
            </Typography>
          )}
        </Box>
      )}
    </FormControl>
  );
};

export default PhoneNumberInput;